/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  CellType,
  ComponentType,
  IMediaListModel,
  Orientation,
  QueryProps,
  ScreenType,
  SourceType,
} from "@nf/common";
import { isServerSideRender } from "@nf/helpers";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useMemo } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import {
  useBrandingFromUrlParameter,
  useGetSortedList,
  useInfiniteScrollData,
  useUpdateMediaListStore,
} from "../../hooks";
import PlaylistSEO from "components/SEO/PlaylistSEO";
import SortingDropdown from "components/Dropdown/SortingDropdown";
import { GridComponent } from "components/Configuration";
import MediaArticleField from "components/Media/MediaArticleField";

import styles from "./PlaylistScreen.module.scss";

const DotsLoader = dynamic(() => import("components/DotsLoader"), {
  ssr: false,
});

export interface PlaylistScreenProps {
  mediaList: IMediaListModel;
}

export const PlaylistScreen = (props: PlaylistScreenProps) => {
  const router = useRouter();
  const params = router.query as QueryProps;
  const mediaListId = params.id ?? -1;
  const playlistId = params.playlist;
  const cellType =
    (params?.cellType?.toUpperCase() as CellType) || CellType.Frame;

  useUpdateMediaListStore(mediaListId, props.mediaList);
  useBrandingFromUrlParameter(params?.channelRoute);

  const queryParams = {
    ...params,
    playlist: playlistId,
    screenType: ScreenType.Playlist,
  };
  const { mediaList, getMore, getList } = useInfiniteScrollData(
    queryParams,
    mediaListId
  );

  const mediaListData = isServerSideRender ? props.mediaList : mediaList;

  // API does not provide any information to check for those lists :(
  const isComingSoon =
    !!mediaListData.Title?.toLowerCase().includes("kommer snart");
  const isLastChance =
    !!mediaListData.Title?.toLowerCase().includes("sidste chance");

  const isSortable =
    mediaListData.SourceType === SourceType.MediaList &&
    !isComingSoon &&
    !isLastChance;

  const hasMore = useMemo(() => {
    return (
      !mediaListData.IsLoading &&
      !!mediaListData.Entities &&
      mediaListData?.Entities.length < mediaListData.TotalCount
    );
  }, [mediaListData]);

  useGetSortedList(getList);

  return (
    <>
      <PlaylistSEO media={mediaListData} />

      <div className={styles.container}>
        <div className={styles.filters}>
          <h1 className={styles.title}>{mediaListData?.Title}</h1>

          {isSortable && (
            <div>
              <SortingDropdown />
            </div>
          )}
        </div>

        <InfiniteScroll
          className={styles.grid}
          dataLength={mediaListData?.Entities?.length || 0}
          next={getMore}
          hasMore={hasMore}
          scrollThreshold={0.6}
          loader={
            <div className={styles.loader}>
              <DotsLoader width={75} />
            </div>
          }
        >
          <GridComponent
            component={{
              ComponentTypeCode: ComponentType.List,
              CellType: cellType,
              Orientation: Orientation.Grid,
              MediaList: mediaListData?.Entities,
              QueryParams: {
                playlist: playlistId,
              },
            }}
          />
        </InfiniteScroll>
      </div>

      {mediaListData.Seo?.Description && (
        <MediaArticleField article={mediaListData.Seo.Description} />
      )}
    </>
  );
};

export default PlaylistScreen;
